import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/rootReducer';
import {
  authInitializationService,
  AuthState,
} from '../services/authService/authInitialization';
import {
  initializeAuth,
  getUserDataRequest,
  getWorkspaceDataRequest,
} from '../store/user/user.action';
import {
  selectIsAuthenticated,
  selectCurrentUser,
  selectWorkspace,
} from '../store/user/user.selector';
import { getAccountRequest } from '../store/accounts/accounts.action';

interface UseAuthInitializationReturn {
  isInitialized: boolean;
  authState: AuthState | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Custom hook for handling auth initialization
 * This hook manages the proper initialization of auth state and data fetching
 */
export const useAuthInitialization = (): UseAuthInitializationReturn => {
  const dispatch = useDispatch();
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redux state
  const isAuthenticated = useSelector((state: RootState) =>
    selectIsAuthenticated(state)
  );
  const currentUser = useSelector((state: RootState) =>
    selectCurrentUser(state)
  );
  const workspace = useSelector((state: RootState) => selectWorkspace(state));

  // Get auth state from service
  const authState = authInitializationService.getAuthState();

  useEffect(() => {
    const initializeAuthFlow = async () => {
      try {
        setError(null);

        // Initialize auth state from localStorage
        authInitializationService.initializeAuthState();

        // Dispatch initialization to Redux
        dispatch(initializeAuth());

        // Check if we need to fetch data
        if (authInitializationService.shouldFetchData()) {
          const { needsUser, needsWorkspace } =
            authInitializationService.getRequiredDataToFetch();

          if (needsUser) {
            dispatch(getUserDataRequest());
          }

          if (needsWorkspace) {
            dispatch(getWorkspaceDataRequest());
          }

          // Fetch account data when user and workspace are being fetched
          if (needsUser || needsWorkspace) {
            dispatch(getAccountRequest());
          }
        }

        setIsInitialized(true);
      } catch (err) {
        console.error('Error initializing auth:', err);
        setError('Failed to initialize authentication');
        setIsInitialized(true); // Still mark as initialized to prevent infinite loading
      }
    };

    if (!isInitialized) {
      initializeAuthFlow();
    }
  }, [dispatch, isInitialized]);

  // Update service state when Redux state changes
  useEffect(() => {
    if (isInitialized && authState) {
      authInitializationService.updateAuthState({
        user: currentUser,
        workspace: workspace,
        isAuthenticated: isAuthenticated,
      });
    }
  }, [isInitialized, currentUser, workspace, isAuthenticated, authState]);

  const isLoading =
    !isInitialized || (isAuthenticated && (!currentUser || !workspace));

  return {
    isInitialized,
    authState,
    isLoading,
    error,
  };
};
